import { useMemo } from "react";
import hljs from "highlight.js";

import S from "./CodeDisplay.module.scss";

interface Choice {
  key?: string;
  start?: number;
  end?: number;
  choice?: string;
}

interface CodeDisplayProps {
  code: string | string[];
  language?: string;
  userSelectedLines?: number[];
  correctLines?: number[];
  choices?: Choice[];
  correctChoiceKey?: string | null;
  incorrectChoiceKey?: string | null;
}

export const CodeDisplay = ({
  code,
  language = "python",
  userSelectedLines = [],
  correctLines = [],
  choices = [],
  correctChoiceKey,
  incorrectChoiceKey,
}: CodeDisplayProps) => {
  const codeLines = useMemo(() => {
    return Array.isArray(code) ? code : code.split("\n");
  }, [code]);

  const lineToChoiceMap = useMemo(() => {
    const map: Record<number, string> = {};
    choices.forEach(choice => {
      const key = choice.key?.toUpperCase();
      if (!key) return;
      if (choice.start !== undefined && choice.end !== undefined) {
        map[choice.start] = key;
      } else if (choice.choice) {
        const line = parseInt(choice.choice, 10);
        if (line) map[line] = key;
      }
    });
    return map;
  }, [choices]);

  const highlightedLines = useMemo(() => {
    return codeLines.map(line => {
      try {
        return hljs.highlight(line, { language }).value;
      } catch {
        return hljs.highlight(line, { language: "plaintext" }).value;
      }
    });
  }, [codeLines, language]);

  const getLineClass = (lineNumber: number) => {
    const isUserLine = userSelectedLines.includes(lineNumber);
    const isCorrectLine = correctLines.includes(lineNumber);
    let lineClass = S.codeLine ?? "";
    if (isUserLine && isCorrectLine) lineClass += ` ${S.userCorrect ?? ""}`;
    else if (isUserLine) lineClass += ` ${S.userIncorrect ?? ""}`;
    else if (isCorrectLine) lineClass += ` ${S.correctAnswer ?? ""}`;
    return lineClass;
  };

  return (
    <div className={S.codeDisplayContainer}>
      <div className={S.codeBlock}>
        <pre className={S.lineNumbers}>
          {highlightedLines.map((_, index) => {
            const lineNumber = index + 1;
            const choiceKey = lineToChoiceMap[lineNumber];
            return (
              <div key={index} className={S.lineNumber}>
                {choiceKey ? (
                  <>
                    <strong
                      className={`${S.lineChoice ?? ""} ${
                        choiceKey === correctChoiceKey?.toUpperCase()
                          ? (S.correctChoice ?? "")
                          : choiceKey === incorrectChoiceKey?.toUpperCase()
                            ? (S.incorrectChoice ?? "")
                            : ""
                      }`}
                    >
                      {choiceKey}
                    </strong>
                    <span>{lineNumber}</span>
                  </>
                ) : (
                  <span>{lineNumber}</span>
                )}
              </div>
            );
          })}
        </pre>
        <pre className={S.codeContent}>
          <code className={`language-${language}`}>
            {highlightedLines.map((line, index) => (
              <span
                key={index}
                className={getLineClass(index + 1)}
                dangerouslySetInnerHTML={{ __html: line || "&nbsp;" }}
              />
            ))}
          </code>
        </pre>
      </div>
    </div>
  );
};
